import { api, processApiData } from './core.js';

// Cache for network stats
const cache = new Map();
const CACHE_TTL = 30000; // 30 seconds cache for AI dashboard
const API_TIMEOUTS = {
  fast: 2000,   // 2s for health, tps
  medium: 5000, // 5s for validators
  slow: 8000    // 8s for holders
};

const cacheWithTTL = (key, data, ttl) => {
  cache.set(key, {
    data,
    timestamp: Date.now(),
    ttl
  });
};

const getCached = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < cached.ttl) {
    return cached.data;
  }
  cache.delete(key);
  return null;
};

// Network statistics API functions
const blockchainStatsApi = {
  // Get network statistics - OPTIMIZED with parallel calls and timeouts
  getNetworkStats: async () => {
    const cacheKey = 'ai-network-stats';
    const cached = getCached(cacheKey);
    if (cached) {
      console.log('Using cached AI network stats');
      return cached;
    }

    try {
      console.log('Fetching network stats from API');
      
      // ⚡ PARALLEL CALLS with timeouts for faster performance
      const [
        healthResult,
        validatorsCountResult,
        tpsResult,
        holdersResult,
        validatorsPayoutResult,
        txCountResult
      ] = await Promise.allSettled([
        // Health data (fast)
        Promise.race([
          api.get('/health'),
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), API_TIMEOUTS.fast))
        ]),
        
        // Validators count (medium)
        Promise.race([
          api.get('/stats/validators/count'),
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), API_TIMEOUTS.medium))
        ]),
        
        // TPS (fast)
        Promise.race([
          api.get('/stats/tps'),
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), API_TIMEOUTS.fast))
        ]),
        
        // Holders (slow but functional)
        Promise.race([
          api.get('/stats/holders'),
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), API_TIMEOUTS.slow))
        ]),
        
        // Validators payout (medium)
        Promise.race([
          api.get('/stats/validators/payout'),
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), API_TIMEOUTS.medium))
        ]),
        
        // Transaction count (medium)
        Promise.race([
          api.get('/stats/transactions/count'),
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), API_TIMEOUTS.medium))
        ])
      ]);
      
      // Process results with fallbacks
      const healthData = healthResult.status === 'fulfilled' ? processApiData(healthResult.value.data) : { lastBlock: 0 };
      const validatorsCountData = validatorsCountResult.status === 'fulfilled' ? processApiData(validatorsCountResult.value.data) : { count: 0 };
      const tpsData = tpsResult.status === 'fulfilled' ? processApiData(tpsResult.value.data) : { tps: 0 };
      const holdersData = holdersResult.status === 'fulfilled' ? processApiData(holdersResult.value.data) : { holders: 0 };
      const validatorsPayoutData = validatorsPayoutResult.status === 'fulfilled' ? processApiData(validatorsPayoutResult.value.data) : { validators: [] };
      const txCountData = txCountResult.status === 'fulfilled' ? processApiData(txCountResult.value.data) : null;
      
      console.log('Health data:', healthData);
      console.log('Validators count data:', validatorsCountData);
      
      // Use latestBlock from validators count if available, otherwise use lastBlock from health
      const latestBlock = validatorsCountData.latestBlock || healthData.lastBlock;
      console.log(`Using latestBlock: ${latestBlock}`);
      
      const tps = tpsData.tps || 0;
      console.log(`TPS: ${tps}`);
      
      const totalHolders = holdersData.holders || 0;
      console.log(`Total holders: ${totalHolders}`);
      
      console.log('Validators payout data:', validatorsPayoutData);
      
      // TEMPORARY SOLUTION: Since validators payout is currently 0, we're using the sum of validators' balances instead
      // TODO: Once validators start receiving payouts, switch back to using totalPayout
      let totalBalance = BigInt(0);
      let formattedBalance = '';
      
      if (validatorsPayoutData.validators && Array.isArray(validatorsPayoutData.validators)) {
        // Sum up all validators' balances
        validatorsPayoutData.validators.forEach(validator => {
          if (validator.balance) {
            totalBalance += BigInt(validator.balance);
          }
        });
        
        // Format the total balance
        const totalBalanceEther = Number(totalBalance) / 1e18;
        formattedBalance = totalBalanceEther.toString();
      }
      
      const validatorsPayout = {
        raw: totalBalance.toString(),
        formatted: formattedBalance
      };
      
      // Handle transaction count from parallel results
      let totalTransactions = 0;
      
      if (txCountData && txCountData.count && typeof txCountData.count === 'number') {
        totalTransactions = txCountData.count;
        console.log(`Got total transactions from dedicated endpoint: ${totalTransactions}`);
      } else {
        console.log('No transaction count from dedicated endpoint, using fallback estimation');
        // Estimate based on latest block (simple fallback)
        totalTransactions = latestBlock * 2; // Conservative estimate
        console.log(`Using fallback estimation for total transactions: ${totalTransactions}`);
      }
      
      // Get gas price with timeout
      let gasPrice = 0;
      try {
        const rpcResponse = await Promise.race([
          api.post('/proxy/rpc', {
            jsonrpc: '2.0',
            method: 'eth_gasPrice',
            params: [],
            id: 1
          }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), API_TIMEOUTS.fast))
        ]);
        
        if (rpcResponse.data && rpcResponse.data.result) {
          const gasPriceWei = parseInt(rpcResponse.data.result, 16);
          gasPrice = gasPriceWei / 1e9; // Convert Wei to Gwei
        }
      } catch (rpcError) {
        console.log('Gas price fetch failed, using default 0');
      }
      
      const result = {
        latestBlock: latestBlock,
        totalTransactions: totalTransactions,
        tps,
        gasPrice: gasPrice,
        totalHolders,
        validatorsPayout
      };
      
      // Cache the result for 30 seconds
      cacheWithTTL(cacheKey, result, CACHE_TTL);
      
      console.log('Network stats compiled successfully');
      return result;
    } catch (error) {
      console.error('Error fetching network stats:', error);
      // Return default values in case of error
      return {
        latestBlock: 0,
        totalTransactions: 0,
        tps: 0,
        gasPrice: 0,
        totalHolders: 0,
        validatorsPayout: 0
      };
    }
  },
  
  // Get contract statistics - OPTIMIZED with parallel calls
  getContractStats: async () => {
    const cacheKey = 'contract-stats';
    const cached = getCached(cacheKey);
    if (cached) {
      console.log('Using cached contract stats');
      return cached;
    }

    try {
      // ⚡ PARALLEL CALLS with timeouts
      const [
        totalContractsResult,
        erc20ContractsResult,
        nftContractsResult
      ] = await Promise.allSettled([
        Promise.race([
          api.get('/stats/contracts/count'),
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), API_TIMEOUTS.medium))
        ]),
        Promise.race([
          api.get('/stats/contracts/erc20/count'),
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), API_TIMEOUTS.medium))
        ]),
        Promise.race([
          api.get('/stats/contracts/nft/count'),
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), API_TIMEOUTS.medium))
        ])
      ]);
      
      // Process results with fallbacks
      const totalContracts = totalContractsResult.status === 'fulfilled' 
        ? (processApiData(totalContractsResult.value.data).count || 0) 
        : 0;
      const erc20Contracts = erc20ContractsResult.status === 'fulfilled' 
        ? (processApiData(erc20ContractsResult.value.data).count || 0) 
        : 0;
      const nftContracts = nftContractsResult.status === 'fulfilled' 
        ? (processApiData(nftContractsResult.value.data).count || 0) 
        : 0;
      
      const result = {
        totalContracts,
        erc20Contracts,
        nftContracts
      };
      
      // Cache for 5 minutes (contract stats change less frequently)
      cacheWithTTL(cacheKey, result, 300000);
      return result;
    } catch (error) {
      console.error('Error fetching contract stats:', error);
      return {
        totalContracts: 0,
        erc20Contracts: 0,
        nftContracts: 0
      };
    }
  },
  
  // Get validator statistics - OPTIMIZED with timeout and caching
  getValidatorStats: async () => {
    const cacheKey = 'validator-stats';
    const cached = getCached(cacheKey);
    if (cached) {
      console.log('Using cached validator stats');
      return cached;
    }

    try {
      // Single call with timeout
      const validatorCountResponse = await Promise.race([
        api.get('/stats/validators/count'),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), API_TIMEOUTS.medium))
      ]);
      
      const validatorCountData = processApiData(validatorCountResponse.data);
      const validatorCount = validatorCountData.count || 0;
      
      const result = { validatorCount };
      
      // Cache for 2 minutes (validator count is relatively stable)
      cacheWithTTL(cacheKey, result, 120000);
      return result;
    } catch (error) {
      console.error('Error fetching validator stats:', error);
      return {
        validatorCount: 0
      };
    }
  }
};

export default blockchainStatsApi;
